import { useEffect, useState } from "react";

const DEFAULT_MESSAGES = [
  "Analyzing your mistakes...",
  "Creating grammar exercises...",
  "Creating vocabulary exercises...",
  "Creating pronunciation exercises...",
  "Creating syntax exercises...",
  "Validating exercises...",
  "Finalizing...",
];

interface Props {
  className?: string;
  messages?: string[];
  subtitle?: string;
}

export default function GenerationLoader({ className = "", messages, subtitle }: Props) {
  const items = messages ?? DEFAULT_MESSAGES;
  const [statusIndex, setStatusIndex] = useState(0);
  const [dots, setDots] = useState(0);

  useEffect(() => {
    const statusInterval = setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % items.length);
    }, 4000);

    const dotsInterval = setInterval(() => {
      setDots((prev) => (prev + 1) % 4);
    }, 500);

    return () => {
      clearInterval(statusInterval);
      clearInterval(dotsInterval);
    };
  }, [items.length]);

  return (
    <div className={`flex flex-col items-center gap-5 py-8 ${className}`}>
      {/* Spinner */}
      <div className="relative w-12 h-12">
        <svg
          className="w-full h-full animate-spin"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="24"
            cy="24"
            r="20"
            stroke="var(--surface2)"
            strokeWidth="4"
          />
          <path
            d="M24 4C35.0457 4 44 12.9543 44 24"
            stroke="var(--accent)"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M44 24C44 35.0457 35.0457 44 24 44"
            stroke="var(--accent)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="4 8"
          />
        </svg>
      </div>

      {/* Status text with cycling dots */}
      <div className="text-center">
        <p className="text-[0.9375rem] text-text-secondary font-medium min-h-[1.5em]">
          {items[statusIndex]}
          <span className="inline-block w-4 text-left">
            {".".repeat(dots)}
          </span>
        </p>
        {subtitle !== undefined ? (
          <p className="text-[0.8125rem] text-text-tertiary mt-1.5">{subtitle}</p>
        ) : (
          <p className="text-[0.8125rem] text-text-tertiary mt-1.5">
            This can take up to 2–3 minutes
          </p>
        )}
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-1.5">
        {items.map((_, i) => (
          <span
            key={i}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
              i === statusIndex
                ? "bg-accent scale-110"
                : i < statusIndex
                ? "bg-accent/40"
                : "bg-surface2"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
